import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/auth/api";
import { alertSuccess, alertError } from "@/components/Alert";

// ✅ Fetch Notifications
export const fetchNotification = createAsyncThunk(
  "notification/fetchNotification",
  async (
    { page = 1, search = "", fetchAll = false },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState().notificationData;
      const { pageSize, cache } = state;

      const cacheKey = `${page}_${search}_${pageSize}_${fetchAll}`;

      // ✅ Cache check
      if (cache[cacheKey]) {
        return {
          data: cache[cacheKey],
          page,
          search,
          pageSize,
          fromCache: true,
        };
      }

      let url = `/surveyHistory/panel/notification`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`;
      }

      if (search) {
        url += `&search=${search}`;
      }

      const response = await api.get(url);

      return {
        data: response.data,
        page,
        search,
        pageSize,
        fromCache: false,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ Add Notification
export const addNotification = createAsyncThunk(
  "notification/addNotification",
  async (newData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/surveyHistory/panel/notification",
        newData
      );
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data?.errors);
      return rejectWithValue(
        error.response?.data?.errors || error.message
      );
    }
  }
);

// ✅ Update Notification
export const updateNotification = createAsyncThunk(
  "notification/updateNotification",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        "/surveyHistory/panel/notification",
        data
      );
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data?.errors);
      return rejectWithValue(
        error.response?.data?.errors || error.message
      );
    }
  }
);

// ✅ Slice
const notificationSlice = createSlice({
  name: "notificationList",
  initialState: {
    notificationList: { result: [], pagination: { total_pages: 1 } },
    search: "",
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    cache: {},
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    setPage: (state, action) => {
      state.currentPage = action.payload;
    },

    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
      state.cache = {}; // ✅ IMPORTANT
    },
  },

  extraReducers: (builder) => {
    builder
      // ✅ Fetch
      .addCase(fetchNotification.pending, (state) => {
        state.status = "loading";
      })

      .addCase(fetchNotification.fulfilled, (state, action) => {
        const { data, page, search, pageSize, fromCache } =
        action.payload;

        const cacheKey = `${page}_${search}_${pageSize}_${false}`;

        if (!fromCache) {
          state.cache[cacheKey] = data;
        }

        state.notificationList = data;
        state.currentPage = page;

        const totalCount = data?.pagination?.count || 0;
        state.totalPages = Math.ceil(totalCount / pageSize);

        state.search = search;
        state.pageSize = pageSize;
        state.status = "succeeded";
      })

      .addCase(fetchNotification.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // ✅ Add
      .addCase(addNotification.pending, (state) => {
        state.status = "loading";
      })

      .addCase(addNotification.fulfilled, (state) => {
        state.status = "succeeded";
        state.cache = {}; // 🔥 clear cache after add
      })

      .addCase(addNotification.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // ✅ Update
      .addCase(updateNotification.pending, (state) => {
        state.status = "loading";
      })

      .addCase(updateNotification.fulfilled, (state) => {
        state.status = "succeeded";
        state.cache = {}; // 🔥 clear cache after update
      })

      .addCase(updateNotification.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, setPage, setPageSize } =
  notificationSlice.actions;

export default notificationSlice.reducer;